import {QuestionResultsLayout as QR} from 'shared/forecast/results'
import {Forecast as F, ResolutionForecasts} from 'shared/forecast/forecast'
import {Bounds} from 'shared/forecast/bounds'
import {compactString} from 'shared/text'
import {concat, keys, mapEntries} from 'shared/collection'
import {NotNull, notNull} from 'shared/type'
import {Dist} from 'shared/chart'
import {z} from 'zod'
import JSZip from 'jszip'
import {DateTime} from 'luxon'
import {isEmpty} from 'remeda'
import {match, P} from 'ts-pattern'
import {CsvRow, objectToCsv} from 'utils/csv'

const toFileSafeToken = (input: string): string => input.replace(/[^a-zA-Z0-9-]/g, '_').trim()

const tsForFile = (ts: number | undefined) =>
  `${(ts == null ? DateTime.utc() : DateTime.fromMillis(ts)).toFormat('yyyy_MM_dd_HH_mm')}_UTC`

const questionItemsForFile = (ts: number | undefined, groups: QR.LayoutGroup[]) => {
  const res = groups
    .map(g => [
      ...((
        g.isRootQuestion ? !g.cfg.disableQuestionForecast : true
      ) ?
        [g.groupResolution.find(r => r.ts == ts)]
          .filter(notNull)
          .map(r => ({type: 'question', id: g.id, cfg: g.cfg, ...r}) as const)
      : []),
      ...g.subqResolutions.flatMap(({id, cfg, parent_id, tsResolutions}) =>
        tsResolutions.filter(r => r.ts == ts).map(r => ({type: 'subq', id, parent_id, cfg, ...r}) as const)
      )
    ])
    .flat()
    .filter(notNull)
  return res
}

type QuestionItemsForFile = ReturnType<typeof questionItemsForFile>[number]
type MakeExportProps = Pick<QR.Layout, 'groups' | 'timestamps'>

export const makeQuestionForecastExport = async ({timestamps, groups}: MakeExportProps) => {
  const zip = new JSZip()
  zip.file(
    'readme.md',
    `
# Question Forecast Export
Created at *${DateTime.utc().toFormat('yyyy-MM-dd HH:mm')} UTC*

This folder contains the following csv files for importing into other tools. 
*Note:*
- all times are in utc
- it is possible to generate an export with the forecasts as of a specific time. This is the [time] value that is in the file name.
- text columns are in double quotes, any double quotes in the string are escaped with another double quote.
- the first row contains the header names


${mapEntries(generators, (_, {help}) => help).join('\n')}
  `
  )

  timestamps
    .map(ts => ({ts, items: questionItemsForFile(ts.ts, groups)}))
    .filter(i => !isEmpty(i.items))
    .forEach(ctx => {
      mapEntries(generators, (name, g) =>
        match(g)
          .with({getRows: P.not(P.nullish)}, ({getRows}) => [{name, rows: getRows(ctx), schema: g.schema}])
          .otherwise(({getFiles}) =>
            getFiles(ctx).map(f => ({name: `${name}.${f.name}`, rows: f.rows, schema: g.schema}))
          )
      )
        .flat()
        .forEach(({name, rows, schema}: {name: string; rows: CsvRow[]; schema: CsvMd['schema']}) =>
          zip.file(
            concat(toFileSafeToken(name), tsForFile(ctx.ts.ts), 'csv').join('.'),
            objectToCsv(rows, keys(schema.shape))
          )
        )
    })
  return await zip.generateAsync({type: 'blob', mimeType: 'application/zip'})
}

type CsvMd = {
  help: string
  schema: z.ZodObject<Record<string, any>>
} & (
  | {getRows: (props: {items: QuestionItemsForFile[]}) => CsvRow[]}
  | {getFiles: (props: {items: QuestionItemsForFile[]}) => {name: string; rows: CsvRow[]}[]}
)

// const questionId = (i: QuestionItemsForFile) =>  (i.type == 'question' ? i.question.id : i.id)
const condition = (r: NotNull<QuestionItemsForFile['resolutions']>[number]) =>
  compactString(r.resolution.labelShort) ?? compactString(r.resolution.label) ?? r.code

const zValue = z.union([z.number(), z.string()]) // because we are exporting, we convert our millis to iso dates to make it easier to work with in other tools
const exportValue = (r: ResolutionForecasts, value: number) =>
  r.cfg.forecastType == 'amount' && r.cfg.amountType == 'date' ? DateTime.fromMillis(value).toUTC().toISO() : value

const exportDate = (r?: string) => (r == null ? '' : DateTime.fromISO(r).toUTC().toISO())

const commonSchema = {
  id: z.string(),
  title: z.string(),
  parent_id: z.string().optional(),
  condition: z.string().optional()
}

const zForecastRow = z.object({
  ...commonSchema,
  created_at: z.string(),
  created_by: z.string(),
  note: z.string().optional(),
  value: zValue
})
type ForecastRow = z.infer<typeof zForecastRow>

const zResultRow = z.object({
  type: z.string(),
  ...commonSchema,
  agg_value: z.union([z.number(), z.string(), z.null()]).optional()
})
type ResultRow = z.infer<typeof zResultRow>

const generators = {
  forecasts: {
    help: `## forecasts.[as-of].csv
A row for each forecast with \`value\` representing the result of the forecast.
- \`id\`: A unique identifier for each question
- \`title\`: The title of the question.
- \`parent_id\`: The id of the parent question for sub-questions
- \`condition\`: For sub-questions this is the label for the condition
- \`created_at\`: The date and time when forecast was made
- \`created_by\`: The person or system that created the question.
- \`note\`: Note the forecaster made when submitting the forecast
- \`value\`: The value of the forecast. For likelihoods this is the percent likely, for amount this is the 50th percentile of the distribution`,
    schema: zForecastRow,
    getRows: ({items}) =>
      items.flatMap(
        i =>
          i.resolutions?.flatMap(r =>
            r.forecasts.flatMap(
              ({created_at, created_by, cfg, value}) =>
                ({
                  id: i.id,
                  title: i.cfg.title,
                  parent_id: i.type == 'subq' ? i.parent_id : undefined,
                  condition: i.type == 'subq' ? condition(r) : undefined,
                  created_at: exportDate(created_at)!,
                  created_by: created_by!,
                  note: cfg.notes,
                  value: exportValue(r, value)!
                }) satisfies ForecastRow
            )
          ) ?? []
      )
  },
  results: {
    help: `## results.[as-of].csv
The aggregate results for each question/sub-question/condition.
- \`type\`: The type of result. Either \`question\` or the subquestion type
- \`id\`: A unique identifier for each question
- \`title\`: The title of the question.
- \`condition\`: For sub-questions this is the label for the condition
- \`agg_value\`: The aggregate value of the forecast. For likelihoods this is geometric mean of adds, for amount this is the 50th percentile of a curve that represents the average of cumulative probability
`,
    schema: zResultRow,
    getRows: ({items}) =>
      items.flatMap(
        i =>
          i.resolutions?.map(
            r =>
              ({
                type: match(i)
                  .with({type: 'subq'}, ({cfg: {subquestionType}}) => subquestionType)
                  .otherwise(() => i.type),
                id: i.id,
                title: i.cfg.title,
                parent_id: i.type == 'subq' ? i.parent_id : undefined,
                condition: i.type == 'subq' ? condition(r) : '',
                agg_value: r.resAgg == null ? undefined : exportValue(r, r.resAgg)
              }) satisfies ResultRow
          ) ?? []
      )
  },
  distribution: {
    help: `## distribution.[as-of].[question_id]-[condition].csv
Aggregated distributions for each question/sub-question/condition that is an amount forecast. A row for each point within a range of x values.
- \`x\`: The value along the x axis of the distribution
- \`p\`: The probability of the value at x 
- \`p_cumulative\`: The cumulative probability of the value at 
    `,
    schema: z.object({
      x: zValue,
      p: z.number(),
      p_cumulative: z.number()
    }),
    getFiles: ({items}) =>
      items.flatMap(
        i =>
          i.resolutions
            ?.map(r =>
              match(r.cfg)
                .with({forecastType: 'amount'}, cfg => ({
                  name: concat(i.id, condition(r)).join('-'),
                  rows: Dist.agg(r.forecasts.map(f => F.amountDist({f: f.cfg, q: cfg})).filter(notNull), {
                    displayBounds: Bounds.toTuple(cfg.defaultBounds),
                    ticks: 1000
                  }).pts.map(({x, p, pAcc}) => ({x: exportValue(r, x), p, p_cumulative: pAcc}))
                }))
                .otherwise(() => undefined)
            )
            .filter(notNull) ?? []
      )
  } as CsvMd
} as Record<string, CsvMd>
